'use client';

import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useCurrentUser,
  useMediaSource,
  type MediaComment,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { EmptyState } from '../_shared/EmptyState';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Comments surface for a video watch page. Heading with the live count,
 * a composer for signed-in users, and the comment list (author, date,
 * body). New comments are added optimistically. Reads
 * {@code MediaSource.comments} + the current user; renders its
 * adapter-missing placeholder when no comments namespace is mounted.
 *
 * <p>Inline-style / CSS-var tokens only (no Tailwind) — matches the
 * restyled card look used across the tube blocks.
 */
export interface CommentsBlockProps {
  videoSlug: string;
  title?: string;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function initialOf(name?: string): string {
  const trimmed = (name ?? '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
}

export function CommentsBlock({ videoSlug, title }: CommentsBlockProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const media = useMediaSource();
  const user = useCurrentUser<{ name?: string }>();
  const [added, setAdded] = useState<MediaComment[]>([]);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = useAsyncResource(
    async (signal) => {
      if (!media?.comments) return null;
      return await media.comments.list(videoSlug, { signal });
    },
    [media, videoSlug],
  );

  if (!media?.comments) {
    return (
      <BlockRoot as="section" style={wrap}>
        <AdapterPlaceholder blockType="CommentsBlock" adapterName="MediaSource" />
      </BlockRoot>
    );
  }

  const fetched = state.kind === 'loaded' ? state.data.items : [];
  const baseTotal = state.kind === 'loaded' ? state.data.total : 0;
  const comments = [...added, ...fetched];
  const total = baseTotal + added.length;

  const submit = async () => {
    const body = text.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    setError(null);
    const optimistic: MediaComment = {
      id: `temp-${Date.now()}`,
      body,
      author: user?.name ?? t('comments.you'),
      createdAt: new Date().toISOString(),
    };
    setAdded((prev) => [optimistic, ...prev]);
    setText('');
    try {
      const saved = await media.comments!.add(videoSlug, body);
      if (saved && saved.id) {
        setAdded((prev) =>
          prev.map((c) => (c.id === optimistic.id ? { ...optimistic, ...saved } : c)),
        );
      }
    } catch (err) {
      // Roll back the optimistic entry and restore the draft.
      setAdded((prev) => prev.filter((c) => c.id !== optimistic.id));
      setText(body);

      console.error('[CommentsBlock] add failed', err);
      setError(t('comments.postFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BlockRoot as="section" style={wrap} aria-label={t('comments.sectionLabel')}>
      <h2 style={heading}>
        {t('comments.count', { count: total })}
        {title ? <span style={{ opacity: 0.6 }}> · {title}</span> : null}
      </h2>

      {user ? (
        <div style={composer}>
          <div style={avatar} aria-hidden>
            {initialOf(user.name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('comments.composerPlaceholder')}
              rows={2}
              style={textarea}
              {...part('field-comment')}
            />
            <div style={composerActions}>
              {error ? (
                <span role="alert" style={errorText}>
                  {error}
                </span>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !text.trim()}
                {...part('submit')}
                style={{
                  ...submitBtn,
                  opacity: submitting || !text.trim() ? 0.55 : 1,
                  cursor: submitting || !text.trim() ? 'default' : 'pointer',
                }}
              >
                {submitting ? t('comments.posting') : t('comments.submit')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={signInHint}>{t('comments.signInHint')}</div>
      )}

      {state.kind === 'loading' && comments.length === 0 ? (
        <div style={{ ...muted, padding: 'var(--space-4, 16px) 0' }}>{t('comments.loading')}</div>
      ) : state.kind === 'error' && comments.length === 0 ? (
        <EmptyState
          title={t('comments.error.title')}
          hint={t('shared.error.tryAgainLater')}
          {...part('empty')}
        />
      ) : comments.length === 0 ? (
        <EmptyState
          title={t('comments.empty.title')}
          hint={t('comments.empty.hint')}
          {...part('empty')}
        />
      ) : (
        <ul style={list} {...part('list')}>
          {comments.map((comment, i) => (
            <li key={comment.id} style={row} {...part('item', i)}>
              <div style={avatar} aria-hidden>
                {initialOf(comment.author)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={rowHead}>
                  <span style={authorName}>{comment.author || t('comments.anonymous')}</span>
                  {comment.createdAt ? (
                    <span style={dateText}>{formatDate(comment.createdAt)}</span>
                  ) : null}
                </div>
                <div style={bodyText}>{comment.body}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </BlockRoot>
  );
}

// ---------------------------------------------------------------------------
// Styles — inline + CSS-var tokens (no Tailwind)
// ---------------------------------------------------------------------------

const wrap: CSSProperties = {
  marginTop: 'var(--space-8, 32px)',
  paddingTop: 'var(--space-6, 24px)',
  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-5, 20px)',
};

const heading: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-lg, 18px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #18181b)',
};

const muted: CSSProperties = {
  color: 'var(--color-text-secondary, #52525b)',
  fontSize: 'var(--font-size-sm, 14px)',
};

const composer: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3, 12px)',
  alignItems: 'flex-start',
};

const avatar: CSSProperties = {
  flex: '0 0 auto',
  width: 36,
  height: 36,
  borderRadius: 'var(--radius-full, 999px)',
  background: 'var(--color-brand-50, #e6efff)',
  color: 'var(--color-text-brand, var(--color-brand-700, #2b3eb0))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '14px',
};

const textarea: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  resize: 'vertical',
  minHeight: 44,
  padding: 'var(--space-2-5, 10px) var(--space-3, 12px)',
  borderRadius: 'var(--radius-md, 8px)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
  background: 'var(--color-surface-card, #fff)',
  color: 'var(--color-text-primary, #18181b)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontFamily: 'inherit',
  lineHeight: 1.4,
};

const composerActions: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 'var(--space-3, 12px)',
  marginTop: 'var(--space-2, 8px)',
};

const submitBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--space-2-5, 10px) var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 8px)',
  border: 0,
  background: 'var(--color-brand-500, #3b5bdb)',
  color: 'var(--color-text-on-brand, #fff)',
  fontSize: '13px',
  fontWeight: 600,
  minHeight: 36,
};

const errorText: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-danger, #dc2626)',
};

const signInHint: CSSProperties = {
  ...muted,
  padding: 'var(--space-3, 12px) var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-card, rgba(127,127,127,0.04))',
  border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
};

const list: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-5, 20px)',
};

const row: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3, 12px)',
  alignItems: 'flex-start',
};

const rowHead: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 'var(--space-2, 8px)',
  flexWrap: 'wrap',
};

const authorName: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #18181b)',
};

const dateText: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-tertiary, #71717a)',
};

const bodyText: CSSProperties = {
  marginTop: 2,
  fontSize: 'var(--font-size-sm, 14px)',
  lineHeight: 1.5,
  color: 'var(--color-text-primary, #27272a)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
